import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Strong,
  Text,
} from '@radix-ui/themes';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonProgress } from 'components/common/progress';
import useBurnInTestStore from 'components/sections/admin-portal/machines/dialogs/settings/machine-burn-in/use-burn-in-test-store';
import { MachineStateSection } from 'components/sections/admin-portal/machines/dialogs/settings/machine-status/machine-state-section';
import { MachineContext } from 'contexts/machine.context';
import { isBefore, parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { QueueState, StaticVideoType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachineStateMsg } from 'lib_ts/interfaces/machine-msg/i-machine-state';
import { SpecialMsPosition } from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { clamp, clone, first } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AdminBurnInTestsService } from 'services/admin/machine-burn-in-tests.service';
import useMachineStatusStore from 'stores/use-machine-status-store';
import { RANGE_TEST_MSS } from './range-test-mss';

const COMPONENT_NAME = 'MachineBurnInRangeTest';

const RESEND_DELAY_MS = 5_000;

export const MachineBurnInRangeTest = () => {
  const queueState = useMachineStatusStore((s) => s.queueState);

  const { sendRawTarget, specialMsTarget } = useContext(MachineContext);

  // while suspended, getting to r2f will not auto-send the next ms
  const [suspended, setSuspended] = useState(false);

  // update this value to trigger a resend
  const [resend, setResend] = useState(Date.now());

  // resend after a delay, clear the timeout when unmounting
  const resendTimeout = useRef<NodeJS.Timeout>();

  // ignore r2f changes until r2fDate is greater than this
  const [sendAttempted, setSendAttempted] = useState(new Date());

  const r2fDate = useMachineStatusStore((s) => s.r2fDate);
  const r2fReady = useMachineStatusStore((s) => s.r2fReady);

  const tests = useBurnInTestStore((s) => s.tests);
  const activeTestID = useBurnInTestStore((s) => s.activeTestID);

  const activeTest = useMemo(
    () => tests.find((m) => m._id === activeTestID),
    [tests, activeTestID]
  );

  const latestTest = useLatest(activeTest);

  const remainingMss = useMemo(
    () =>
      activeTest?.rangeTest
        ? RANGE_TEST_MSS.map(
            // ! remove this for prod; limit px to +/- 2.9 for testing at LW
            (m) => ({
              ...m,
              px: clamp(m.px, -2.9, 2.9),
            })
          ).filter((m) => !activeTest.rangeTest?.processed[m.id])
        : [],
    [activeTest?.rangeTest, RANGE_TEST_MSS]
  );

  const activeMs = first(remainingMss);

  // auto send the active ms if the machine is not in error
  useEffect(() => {
    const callback = async () => {
      if (queueState !== QueueState.Active) {
        console.debug(`${COMPONENT_NAME}: skipped send, not active on machine`);
        return;
      }

      if (!activeMs) {
        console.debug(`${COMPONENT_NAME}: skipped send, no active ms`);
        return;
      }

      if (suspended) {
        console.debug(`${COMPONENT_NAME}: skipped send, suspended`);
        return;
      }

      setSendAttempted(new Date());

      const msg: IMachineStateMsg = {
        ...activeMs,
        hash: null,
        force: null,
        jitter: null,
        pitch_info: null,
        // todo: do we need a real video for this?
        video_uuid: StaticVideoType.training_short,
      };

      const success = await sendRawTarget(msg, COMPONENT_NAME, true);

      if (success) {
        // now wait for a r2fReady
        return;
      }

      // attempt to resend until it works
      console.warn(
        `${COMPONENT_NAME}: send failed, scheduling resend in ${RESEND_DELAY_MS / 1000}s`
      );
      resendTimeout.current = setTimeout(() => {
        setResend(Date.now());
      }, RESEND_DELAY_MS);
    };

    callback();

    return () => {
      clearTimeout(resendTimeout.current);
    };
  }, [
    queueState,
    activeMs,
    resend,
    suspended, // toggling suspension will auto-resend the active ms
  ]);

  // after successfully achieving r2f, mark the active ms's full hash as tested
  useEffect(() => {
    const callback = async () => {
      if (suspended) {
        console.debug(`${COMPONENT_NAME}: skipping r2f update, suspended`);
        return;
      }

      if (!r2fReady) {
        console.debug(`${COMPONENT_NAME}: skipping r2f update, not ready`);
        return;
      }

      if (!r2fDate) {
        console.debug(`${COMPONENT_NAME}: skipping r2f update, no r2f date`);
        return;
      }

      if (isBefore(r2fDate, sendAttempted)) {
        console.debug(
          `${COMPONENT_NAME}: skipping r2f update, r2f date is before send attempt`
        );
        return;
      }

      if (!activeTestID) {
        console.debug(`${COMPONENT_NAME}: skipping r2f update, no active test`);
        return;
      }

      if (!activeMs) {
        console.debug(`${COMPONENT_NAME}: skipping r2f update, no active ms`);
        return;
      }

      if (!activeMs.full_hash) {
        console.debug(
          `${COMPONENT_NAME}: skipping r2f update, no active ms full hash`
        );
        return;
      }

      if (!latestTest.current?.rangeTest) {
        console.debug(
          `${COMPONENT_NAME}: skipping r2f update, no latest range test`
        );
        return;
      }

      const nextRangeTest = clone(latestTest.current.rangeTest);
      nextRangeTest.processed[activeMs.id] = new Date().toISOString();

      AdminBurnInTestsService.getInstance()
        .putOne(activeTestID, {
          rangeTest: nextRangeTest,
        })
        .then((updatedTest) => {
          if (!updatedTest) {
            console.warn(`${COMPONENT_NAME}: failed to update test`);
            return;
          }

          console.debug(`${COMPONENT_NAME}: updating active test`, updatedTest);
          useBurnInTestStore.getState().updateTest(updatedTest);
        });
    };

    const timeout = setTimeout(() => {
      callback();
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [activeTestID, suspended, activeMs, r2fDate, r2fReady]);

  // when there are no more remaining pitches, mark the test as completed
  useEffect(() => {
    if (remainingMss.length > 0) {
      // there are still mss remaining, do nothing
      return;
    }

    if (!activeTestID) {
      console.debug(`${COMPONENT_NAME}: skipping end update, no active test`);
      return;
    }

    if (latestTest.current?.end) {
      console.debug(`${COMPONENT_NAME}: skipping end update, already ended`);
      return;
    }

    AdminBurnInTestsService.getInstance()
      .putOne(activeTestID, {
        end: new Date().toISOString(),
      })
      .then((updatedTest) => {
        if (!updatedTest) {
          console.warn(`${COMPONENT_NAME}: failed to update test`);
          return;
        }

        console.debug(`${COMPONENT_NAME}: updating active test`, updatedTest);
        useBurnInTestStore.getState().updateTest(updatedTest);
      });
  }, [activeTestID, remainingMss.length]);

  const progress =
    RANGE_TEST_MSS.length > 0
      ? (RANGE_TEST_MSS.length - remainingMss.length) / RANGE_TEST_MSS.length
      : 0;

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonBackArrow
        onClick={() => useBurnInTestStore.setState({ activeTestID: null })}
      />

      <Card size="3">
        <Flex direction="column" gap="4">
          <Flex gap="2" justify="between" align="center">
            <Heading>Range Test</Heading>

            {activeTest?.start && (
              <Badge>
                Start:{' '}
                {format(parseISO(activeTest.start), LOCAL_DATETIME_FORMAT, {
                  timeZone: LOCAL_TIMEZONE,
                })}
              </Badge>
            )}
          </Flex>

          <CommonCallout
            content={
              <Text>
                The machine will automatically move through a pre-determined set
                of machine states without firing.
                <br />
                <Strong>
                  Please ensure the area is clear while the test is underway.
                </Strong>
              </Text>
            }
          />

          <CommonProgress
            value={progress * 100}
            leftLabel={
              RANGE_TEST_MSS.length > 0 ? (
                <Text truncate>
                  Progress:{' '}
                  {activeMs ? (
                    <Badge className="font-mono">{activeMs.id}</Badge>
                  ) : (
                    <Badge color={RADIX.COLOR.SUCCESS}>Complete</Badge>
                  )}
                </Text>
              ) : (
                // pitches are loading
                <Text>Please wait...</Text>
              )
            }
            rightLabel={<Text>{remainingMss.length} remaining</Text>}
          />

          <Flex gap="2" align="center">
            <Box minWidth="120px">
              <Button
                className="btn-block"
                color={RADIX.COLOR.WARNING}
                variant={RADIX.BUTTON.VARIANT.SECONDARY}
                onClick={() => {
                  // pause auto-sends until the problem is resolved
                  setSuspended(true);
                  specialMsTarget(SpecialMsPosition.lowered);
                }}
              >
                {t('common.lower-machine')}
              </Button>
            </Box>

            <Box flexGrow="1"></Box>

            <Box minWidth="120px">
              <Button
                className="btn-block"
                color={RADIX.COLOR.SUCCESS}
                disabled={!suspended}
                onClick={() => {
                  // unpause auto-sends
                  setSuspended(false);
                }}
              >
                Resume
              </Button>
            </Box>
          </Flex>
        </Flex>
      </Card>

      <MachineStateSection lastTarget={activeMs} />
    </ErrorBoundary>
  );
};
